import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useApi } from "../utils/Api";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const UPLOADS_BASE_URL = "https://kinewsstudiolimited.co.uk";

function buildUploadedFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${UPLOADS_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

export default function Checkout() {
  const { items, totals } = useCart();
  const { apiFetch } = useApi();

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const hasItems = items.length > 0;

  const grandTotal = useMemo(() => Number(totals.subtotal || 0), [totals]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSubmitError("");
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.full_name.trim()) {
      nextErrors.full_name = "Full name is required";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!form.phone.trim()) {
      nextErrors.phone = "Phone number is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateCartUploads = () => {
    for (const item of items) {
      const uploadedFiles = Array.isArray(item.uploaded_files)
        ? item.uploaded_files
        : [];

      for (const file of uploadedFiles) {
        if (!file?.file_path && !file?.temp_path) {
          throw new Error(
            `An uploaded file for "${item.name}" is missing. Please remove the item and add it again.`,
          );
        }
      }
    }
  };

  const buildCheckoutItems = () => {
    return items.map((item) => ({
      product_id: Number(item.id),
      qty: Number(item.qty || 1),
      selected_options: Array.isArray(item.selected_options)
        ? item.selected_options.map((option) => ({
            id: Number(option.id),
            label: option.label,
            price: Number(option.price || 0),
          }))
        : [],
      custom_text:
        typeof item.custom_text === "string" ? item.custom_text.trim() : "",
      uploaded_files: Array.isArray(item.uploaded_files)
        ? item.uploaded_files
            .filter((file) => file.file_path || file.temp_path)
            .map((file) => ({
              name: file.name || "",
              file_path: file.file_path || file.temp_path,
              temp_path: file.temp_path || file.file_path,
            }))
        : [],
    }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (!hasItems || submitting) return;
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setSubmitError("");

      validateCartUploads();

      const payload = {
        customer_name: form.full_name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        notes: form.notes.trim(),
        items: JSON.stringify(buildCheckoutItems()),
      };

      const res = await apiFetch("create_shop_checkout_session", payload);

      if (!res?.success || !res?.checkout_url) {
        throw new Error(res?.message || "Unable to start checkout.");
      }

      window.location.href = res.checkout_url;
    } catch (err) {
      setSubmitError(err.message || "Unable to start checkout.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = submitting ? "Starting payment..." : "Continue to payment";

  return (
    <main className="min-h-screen bg-[#f5f5f5] mt-20 text-black">
      <section className="bg-black py-8 text-white">
        <div className="mx-auto max-w-7xl px-5 pb-12 pt-4 md:pt-6">
          <p className="text-sm uppercase tracking-[0.2em] text-white/60">
            Checkout
          </p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">
            Complete your order
          </h1>
          <p className="mt-3 text-white/70">
            Enter your details and continue to secure payment.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10">
        {!hasItems ? (
          <div className="rounded-3xl border border-black/10 bg-white p-8">
            <h2 className="text-2xl font-semibold">Your cart is empty</h2>
            <p className="mt-2 text-black/60">
              Add some items before proceeding to checkout.
            </p>

            <div className="mt-6">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center rounded-2xl bg-black px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Go to shop
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <form
                onSubmit={handlePlaceOrder}
                className="rounded-3xl border border-black/10 bg-white p-6 md:p-8"
              >
                <h2 className="text-2xl font-semibold">Your details</h2>
                <p className="mt-2 text-sm text-black/60">
                  Fill in your information to continue with your order.
                </p>

                <div className="mt-8 grid gap-5">
                  <div>
                    <label
                      htmlFor="full_name"
                      className="mb-2 block text-sm font-semibold text-black"
                    >
                      Full name
                    </label>
                    <input
                      id="full_name"
                      type="text"
                      value={form.full_name}
                      onChange={(e) => updateField("full_name", e.target.value)}
                      className={[
                        "w-full rounded-2xl border px-4 py-3 text-sm outline-none transition",
                        errors.full_name
                          ? "border-red-500"
                          : "border-black/15 focus:border-black/35",
                      ].join(" ")}
                      placeholder="Enter your full name"
                    />
                    {errors.full_name && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.full_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-semibold text-black"
                    >
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={[
                        "w-full rounded-2xl border px-4 py-3 text-sm outline-none transition",
                        errors.email
                          ? "border-red-500"
                          : "border-black/15 focus:border-black/35",
                      ].join(" ")}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-sm font-semibold text-black"
                    >
                      Phone number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      className={[
                        "w-full rounded-2xl border px-4 py-3 text-sm outline-none transition",
                        errors.phone
                          ? "border-red-500"
                          : "border-black/15 focus:border-black/35",
                      ].join(" ")}
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="notes"
                      className="mb-2 block text-sm font-semibold text-black"
                    >
                      Notes
                      <span className="ml-1 text-black/50">(optional)</span>
                    </label>
                    <textarea
                      id="notes"
                      rows={5}
                      value={form.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      className="w-full resize-none rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none transition focus:border-black/35"
                      placeholder="Add any extra information here"
                    />
                  </div>
                </div>

                {submitError && (
                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/cart"
                    className="inline-flex items-center justify-center rounded-2xl border border-black/15 px-5 py-3 text-sm font-semibold transition hover:border-black/30"
                  >
                    Back to cart
                  </Link>

                  <button
                    type="submit"
                    disabled={submitting}
                    className={[
                      "inline-flex items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold transition",
                      submitting
                        ? "cursor-not-allowed bg-black/40 text-white"
                        : "bg-black text-white hover:opacity-90",
                    ].join(" ")}
                  >
                    {submitLabel}
                  </button>
                </div>
              </form>
            </div>

            <aside className="lg:col-span-5">
              <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-7 lg:sticky lg:top-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-black/50">
                  Order summary
                </p>

                <div className="mt-5 space-y-4">
                  {items.map((item) => {
                    const unitPrice = Number(item.final_price ?? item.price ?? 0);
                    const lineTotal = unitPrice * Number(item.qty || 1);
                    const selectedOptions = Array.isArray(item.selected_options)
                      ? item.selected_options
                      : [];
                    const uploadedFiles = Array.isArray(item.uploaded_files)
                      ? item.uploaded_files
                      : [];
                    const customText =
                      typeof item.custom_text === "string"
                        ? item.custom_text.trim()
                        : "";

                    return (
                      <div
                        key={item.key}
                        className="rounded-2xl border border-black/10 p-4"
                      >
                        <div className="flex gap-3">
                          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-black/[0.04]">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-cover"
                              />
                            ) : null}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate font-semibold">
                                  {item.name}
                                </p>

                                {selectedOptions.length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {selectedOptions.map((option, index) => (
                                      <p
                                        key={`${option.id || option.label || index}_${index}`}
                                        className="text-xs text-black/60"
                                      >
                                        {option.label}
                                      </p>
                                    ))}
                                  </div>
                                )}

                                {customText && (
                                  <div className="mt-2">
                                    <p className="text-[10px] uppercase text-black/40">
                                      Custom text
                                    </p>
                                    <p className="text-xs text-black/70 whitespace-pre-wrap">
                                      {customText}
                                    </p>
                                  </div>
                                )}

                                {uploadedFiles.length > 0 && (
                                  <div className="mt-2">
                                    <p className="mb-2 text-[10px] uppercase text-black/40">
                                      Uploaded image
                                      {uploadedFiles.length > 1 ? "s" : ""}
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                      {uploadedFiles.map((file, index) => {
                                        const preview =
                                          file?.preview ||
                                          buildUploadedFileUrl(
                                            file?.file_path || file?.temp_path,
                                          ) ||
                                          (typeof file === "string" ? file : "");

                                        if (!preview) return null;

                                        return (
                                          <div
                                            key={`${file?.name || "upload"}_${index}`}
                                            className="relative h-10 w-10 overflow-hidden rounded-lg border border-black/10"
                                          >
                                            <img
                                              src={preview}
                                              alt={`Upload ${index + 1}`}
                                              className="h-full w-full object-cover"
                                            />
                                            {(file?.uploaded || file?.file_path) && (
                                              <span className="absolute bottom-0.5 right-0.5 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="text-right">
                                <p className="text-sm font-semibold">
                                  {money(lineTotal)}
                                </p>
                                <p className="mt-1 text-xs text-black/50">
                                  Qty: {item.qty}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 border-t border-black/10 pt-5">
                  <div className="flex items-center justify-between text-sm text-black/70">
                    <span>Items</span>
                    <span>{totals.count}</span>
                  </div>

                  <div className="mt-2 flex items-center justify-between text-sm text-black/70">
                    <span>Subtotal</span>
                    <span>{money(totals.subtotal)}</span>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-black/10 pt-4">
                    <span className="text-base font-semibold">Total</span>
                    <span className="text-xl font-bold">
                      {money(grandTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </main>
  );
}